import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import DateInput from "@/components/reusables/custom-form-fields/DateInput";
import {
  FormSelectSingle,
  SelectMulti,
} from "@/components/reusables/custom-form-fields/FormSelect";
import FormWrapper from "@/components/reusables/custom-form-fields/FormWrapper";
import { Button } from "@/components/ui/button";
import { Form, FormField } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { ESTIMATE_CONST } from "@/constants/estimates.constant";
import { Button_Names } from "@/constants/global-constants";
import { FollowUpIntervals } from "@/constants/project-register.constant";
import useGetFilter from "@/hooks/useGetFilter";
import useQueryParams from "@/hooks/useQueryParams";
import { FollowUpDetails } from "@/interfaces/project-register.interface";
import { cn } from "@/lib/utils";
import { TupdateDetailData } from "@/pages/task-and-claims/context/TaskClaimsCtx";
import {
  claimsApi,
  useMutateClaimsDetailsMutation,
} from "@/redux/api/claimsQuery";
import { useMutateEstimateMutation } from "@/redux/api/prQuery";
import {
  FollowUpSchema,
  TFollowUpSchema,
} from "@/schemas/project-register.schema";
import { parseToFormData } from "@/utils/helper-functions/form.helper";
import { getList } from "@/utils/helpers";

type TFollowPopover = {
  leadsId: string;
  estimateId: string;
  followUpRequired: boolean;
  followUpDetails: FollowUpDetails | undefined;
  isClaims?: boolean;
  isFinal: boolean;
  disabled?: boolean;
};

const FollowPopover = ({
  leadsId,
  estimateId,
  followUpRequired,
  followUpDetails,
  isClaims = false,
  isFinal = false,
  disabled,
}: TFollowPopover): React.JSX.Element => {
  const mainValues: TFollowUpSchema = followUpRequired
    ? {
        isFollowUpRequired: followUpRequired,
        followUpDetails: {
          id: followUpDetails?.id,
          teamMembers:
            followUpDetails?.teamMembers.map((item) => item.id) ?? [],
          intervals: followUpDetails?.intervals || FollowUpIntervals.EVERY_DAY,
          startingDate: followUpDetails?.startingDate || new Date(),
        },
      }
    : {
        isFollowUpRequired: false,
      };

  const form = useForm<TFollowUpSchema>({
    resolver: zodResolver(FollowUpSchema),
    defaultValues: {
      isFollowUpRequired: false,
    },
    values: mainValues,
  });

  // const { id: claimsId } = useQueryID("rowId");
  const { keys } = useQueryParams();
  const claimsId = keys?.rowId as string;

  const { adminsList, managersList } = useGetFilter(["admin", "manager"]);
  const [mutateClaimsDetails] = useMutateClaimsDetailsMutation();

  const updateDetailData = async ({
    data,
    claimsId,
    claimsType,
    claimsDetailId,
    successMsg,
  }: TupdateDetailData): Promise<void> => {
    // if (!claimsDetailId || !claimsId) {
    //   return;
    // }

    const res = await mutateClaimsDetails({
      method: "PUT",
      data,
      claimsId,
      claimsType,
      claimsDetailId,
    });

    if (res.data) {
      toast.success(successMsg);
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const isFollowUpRequired = form.watch("isFollowUpRequired");
  const [mutateEstimate, { isLoading }] = useMutateEstimateMutation();

  const membersList = useMemo(() => {
    const managers = getList(managersList);
    const admins = getList(adminsList);

    return [...managers, ...admins];
  }, [adminsList, managersList]);

  const handleExit = (): void => {
    if (disabled) {
      return;
    }
    form.reset();
    setIsOpen(!isOpen);
  };

  const dispatch = useDispatch();
  const defaultMembers = form.getValues("followUpDetails.teamMembers");

  const onSubmit = async (data: TFollowUpSchema): Promise<void> => {
    // if (!leadsId) {
    //   return;
    // }
    if (isFinal) {
      toast.error(ESTIMATE_CONST.MESSAGES.ES_FINAL_NO_UPDATE);

      return;
    }

    const formData = parseToFormData(data);
    // if it is for task and claims drafts followup
    if (isClaims || claimsId) {
      await updateDetailData({
        data: formData,
        claimsId: claimsId || "",
        claimsType: "estimate",
        claimsDetailId: estimateId,
        successMsg: ESTIMATE_CONST.MESSAGES.FOLLOWUP_UPDATED,
      });

      dispatch(claimsApi.util.invalidateTags(["drafts"]));

      return;
    }

    const res = await mutateEstimate({
      id: leadsId,
      data: formData,
      estimateId,
    });

    if (res.data) {
      toast.success(ESTIMATE_CONST.MESSAGES.FOLLOWUP_UPDATED);
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleExit}>
      <PopoverTrigger asChild>
        <div
          onClick={() => {
            if (disabled) {
              return;
            }
            setIsOpen(!isOpen);
          }}
          className={cn(
            disabled && "cursor-not-allowed",
            !disabled && "cursor-pointer",
          )}
        >
          {followUpRequired ? "YES" : "NO"}
        </div>
      </PopoverTrigger>

      <PopoverContent className='min-w-[450px] space-y-3 mx-4'>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='flex items-center gap-4'>
              <div className='w-5/6 space-y-2'>
                <h2 className='font-semibold '>Follow Up</h2>
                <p className='text-sm text-aldgray'>
                  You&apos;ll be reminded about deadlines of this quote
                </p>
              </div>
              <FormField
                name={`isFollowUpRequired`}
                control={form.control}
                render={({ field }) => (
                  <FormWrapper>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormWrapper>
                )}
              />
            </div>

            {isFollowUpRequired && (
              <div className='space-y-4'>
                <FormWrapper>
                  <SelectMulti
                    control={form.control}
                    name='followUpDetails.teamMembers'
                    defaultValue={defaultMembers}
                    label='Team Members'
                    placeholder='Select Team Members'
                    className='w-full '
                    options={membersList}
                    isRequired
                    maxCount={2}
                  />
                </FormWrapper>

                <FormField
                  control={form.control}
                  name='followUpDetails.intervals'
                  render={({ field }) => (
                    <FormWrapper label='Intervals' isRequired>
                      <FormSelectSingle
                        placeholder='Select Intervals'
                        field={field}
                        options={[
                          {
                            label: "Every Daily",
                            value: FollowUpIntervals.EVERY_DAY,
                          },
                          {
                            label: "Every Weekly",
                            value: FollowUpIntervals.EVERY_WEEK,
                          },
                          {
                            label: "Every Monthly",
                            value: FollowUpIntervals.EVERY_MONTH,
                          },
                        ]}
                      />
                    </FormWrapper>
                  )}
                />

                <FormField
                  name='followUpDetails.startingDate'
                  control={form.control}
                  render={({ field }) => (
                    <FormWrapper label='Start On' isRequired>
                      <DateInput
                        className='min-w-full'
                        defaultValue={field.value as Date}
                        onChange={(date) => field.onChange(date)}
                        dateLimit={
                          new Date(new Date().getTime() - 24 * 60 * 60 * 1000)
                        }
                      />
                    </FormWrapper>
                  )}
                />
              </div>
            )}

            {form.formState.isDirty && (
              <div className='flex flex-row-reverse gap-2 mt-4'>
                <Button isLoading={isLoading} type='submit'>
                  {Button_Names.Save_Changes}
                </Button>

                <Button
                  variant={"ghost"}
                  isLoading={isLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    form.reset();
                  }}
                  type='button'
                >
                  {Button_Names.Reset}
                </Button>
              </div>
            )}
          </form>
        </Form>
      </PopoverContent>
    </Popover>
  );
};

export default FollowPopover;
